import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Typography, CircularProgress, Stack } from '@mui/material';
import { useParams } from 'react-router-dom';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { store } from '../../config/firebase';
import GameByStatusCards from '../ui/gameStatusCards/gameByStatusCards';
import GameByStatusCardPublicBasic from '../ui/gameStatusCards/gameByStatusCardPublicBasic';
import { signOutUser } from '../../services/auth';
import { useColorMode } from '../../hooks/colorModeProvider';

const PublicScoringRink = () => {
  const { eventId, greenRink } = useParams();
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const dispatch = useDispatch();

  const { mode, setMode } = useColorMode();

  useEffect(() => {
    setMode('light');
  }, [mode, setMode]);

  useEffect(() => {
    if (!eventId || !greenRink) {
      setError('Missing event ID or rink parameter');
      setLoading(false);
      return;
    }

    // Extract green and rink number from the rink parameter (e.g., "A1" -> green="A", rink="1")
    const greenMatch = greenRink.match(/^([A-Z]+)(\d+)$/);

    if (!greenMatch) {
      setError('Invalid rink format. Expected format: A1, B2, etc.');
      setLoading(false);
      return;
    }

    const [, green, rinkNumber] = greenMatch;
    const rinkAsNumber = parseInt(rinkNumber, 10);

    const gamesRef = collection(store, 'games');
    const q = query(
      gamesRef,
      where('event', '==', eventId),
      where('green', '==', green),
      where('rink', '==', rinkAsNumber)
    );

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const gamesData = [];
        querySnapshot.forEach((doc) => {
          gamesData.push({ id: doc.id, ...doc.data() });
        });

        // Sort games so completed games (status = 3) appear at the end
        const sortedGames = gamesData.sort((a, b) => {
          // If one game is completed (status 3) and the other isn't, put completed at end
          if (a.status === 3 && b.status !== 3) return 1;
          if (a.status !== 3 && b.status === 3) return -1;

          // If both have same completion status, maintain original order
          return 0;
        });

        setGames(sortedGames);
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error('Error fetching games:', err);
        setError('Failed to load games');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [eventId, greenRink]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading games...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <Typography variant="h6" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      display={'flex'}
      flexDirection={'column'}
      justifyContent={'center'}
      alignItems={'center'}
      sx={{ p: 2 }}>
      <Typography variant="h4" gutterBottom>
        Scoring - {greenRink}
      </Typography>

      {games.length === 0 ? (
        <Typography variant="h6">No games found for rink {greenRink}</Typography>
      ) : (
        <Stack alignItems={'center'} gap={2}>
          <Typography variant="h6" gutterBottom>
            Games on {greenRink} ({games.length} game{games.length !== 1 ? 's' : ''})
          </Typography>

          {games.map((game) => (
            <GameByStatusCardPublicBasic key={game.id} game={game} />
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default PublicScoringRink;
